import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { StringData } from "../../core/StringData";
import { frameDOM } from "../../utils/frame-dom";
import { styleTxn } from "../../utils/style-txn";
import { EasingFunctionOutput } from "../../tools/EasingFunctionTool";

const RESIZE_DEBOUNCE_MS = 20;
const CONSTANTS = {
  MANUAL_COLS_KEY: "masonry-manual-cols",
  MANUAL_GAP_KEY: "masonry-manual-gap",
  DEFAULT_DURATION: 600,
  DEFAULT_EASING: "cubic-bezier(0.25, 1, 0.5, 1)",
} as const;

interface MasonryItemState {
  sx: number;
  sy: number;
  sw: number;

  tx: number;
  ty: number;
  tw: number;

  cx: number;
  cy: number;
  cw: number;

  dx: number;
  dy: number;
  dw: number;

  startTime: number;

  posEase: EasingFunctionOutput;
  invPosDur: number;

  sizeEase: EasingFunctionOutput;
  invSizeDur: number;

  isMoving: boolean;
}

interface MasonryState {
  ro: ResizeObserver;
  mo: MutationObserver;
  resizeDebounceId?: number;
  isResizing: boolean;

  lastObservedWidth: number;
  lastObservedHeight: number;
  lastLayoutColumns: number;
  lastLayoutGap: number;
  lastLayoutColumnWidth: number;
  lastChildrenHash: number;
  lastHeightsHash: number;

  childIds: WeakMap<HTMLElement, number>;
  nextChildId: number;

  imgUnsubs: WeakMap<HTMLElement, () => void>;
  items: Map<HTMLElement, MasonryItemState>;

  startHeight: number;
  targetHeight: number;
  heightStartTime: number;
  heightDuration: number;
  heightEase: EasingFunctionOutput;
  isAnimatingHeight: boolean;

  isAnimating: boolean;
  layoutScheduled: boolean;

  pendingHeight?: number;
  pendingHeightAnimate: boolean;

  expectedEndTime: number;
  endTimerId?: number;
}

interface ResponsiveValue<T> {
  breakpoint: number;
  value: T;
}

interface GridSettings {
  columns: number;
  gap: number;
}

function parseTime(val: string | null): number {
  if (!val) return CONSTANTS.DEFAULT_DURATION;
  const n = parseFloat(val);
  return isNaN(n) ? CONSTANTS.DEFAULT_DURATION : n;
}

function getResponsiveValue<T>(data: Array<ResponsiveValue<T>>, windowWidth: number): T {
  const sorted = [...data].sort((a, b) => b.breakpoint - a.breakpoint);
  const matched = sorted.find((e) => windowWidth >= e.breakpoint);
  return matched ? matched.value : sorted[sorted.length - 1]?.value;
}

export class StringMasonry extends StringModule {
  private states = new WeakMap<StringObject, MasonryState>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "masonry";
    this.attributesToMap.push(
      { key: "masonry-cols", type: "breakpoint-dimension", fallback: "2|640:3|1024:4" },
      { key: "masonry-gap", type: "breakpoint-dimension", fallback: "16|640:24|1024:32" },
      { key: "masonry-mode", type: "string", fallback: "auto" },
    );
  }

  private parseEasing(val: string | null): EasingFunctionOutput {
    return this.tools.easingFunction.process({ easing: val || CONSTANTS.DEFAULT_EASING });
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    const el = object.htmlElement as HTMLElement;

    styleTxn.begin();
    styleTxn.setProps(el, {
      position: "relative",
      boxSizing: "border-box",
    });
    styleTxn.commit();

    const state = this.createState(object, el);
    this.states.set(object, state);

    this.attachImgLoaders(object, state, el);
    this.scheduleLayout(object, false, "init");

    const id = object.id;
    this.events.on(
      `masonry:update:${id}`,
      (data: { mode?: string; cols?: number; gap?: number }) => {
        if (data.mode) object.setProperty("masonry-mode", data.mode);
        if (data.cols !== undefined)
          object.setProperty(CONSTANTS.MANUAL_COLS_KEY, Number(data.cols));
        if (data.gap !== undefined) object.setProperty(CONSTANTS.MANUAL_GAP_KEY, Number(data.gap));
        this.scheduleLayout(object, true, "external-event");
      },
    );
  }

  override onFrame(data: StringData): void {
    const timeNow = data.time.now;

    this.objectsOnPage.forEach((object) => {
      const state = this.states.get(object);
      if (!state || !state.isAnimating) return;

      let activeAnimations = 0;

      styleTxn.run(() => {
        if (state.isAnimatingHeight) {
          const elapsed = timeNow - state.heightStartTime;
          const progress = Math.min(1, elapsed / state.heightDuration);
          const ease = state.heightEase(progress);
          const currentH = state.startHeight + (state.targetHeight - state.startHeight) * ease;

          styleTxn.setProps(object.htmlElement, { height: `${currentH}px` });

          if (progress < 1) activeAnimations++;
          else state.isAnimatingHeight = false;
        }

        state.items.forEach((item, el) => {
          if (!item.isMoving) return;

          const elapsed = timeNow - item.startTime;

          const pProgressRaw = elapsed * item.invPosDur;
          const pProgress = pProgressRaw > 1 ? 1 : pProgressRaw;
          const pEaseVal = item.posEase(pProgress);
          item.cx = item.sx + item.dx * pEaseVal;
          item.cy = item.sy + item.dy * pEaseVal;

          const sProgressRaw = elapsed * item.invSizeDur;
          const sProgress = sProgressRaw > 1 ? 1 : sProgressRaw;
          const sEaseVal = item.sizeEase(sProgress);
          item.cw = item.sw + item.dw * sEaseVal;

          styleTxn.setProps(el, {
            transform: `translate3d(${item.cx}px, ${item.cy}px, 0)`,
            ...(Math.abs(item.dw) > 0.05 ? { width: `${item.cw}px` } : {}),
            willChange: "transform, width",
          });

          if (pProgress < 1 || sProgress < 1) {
            activeAnimations++;
          } else {
            item.isMoving = false;
            styleTxn.setProps(el, {
              transform: `translate3d(${item.tx}px, ${item.ty}px, 0)`,
              width: `${item.tw}px`,
              willChange: "auto",
            });
            item.cx = item.tx;
            item.cy = item.ty;
            item.cw = item.tw;
          }
        });
      });

      if (activeAnimations === 0) {
        this.handleAnimationEnd(object, state, timeNow);
      }
    });
  }

  override onResize(): void {
    if (this.data.system.suppressMasonryResize) return;
    this.objectsOnPage.forEach((o) => {
      if (this.states.has(o)) this.scheduleLayout(o, true, "window-resize");
    });
  }

  cleanupObject(object: StringObject): void {
    const state = this.states.get(object);
    if (!state) return;
    state.ro.disconnect();
    state.mo.disconnect();
    if (state.resizeDebounceId) clearTimeout(state.resizeDebounceId);
    this.states.delete(object);
  }

  private createState(object: StringObject, el: HTMLElement): MasonryState {
    const initialRect = el.getBoundingClientRect();

    const roCallback: ResizeObserverCallback = (entries) => {
      if (!entries.length) return;
      const rect = entries[0].contentRect;
      const state = this.states.get(object);
      if (!state) return;

      if (
        Math.abs(rect.width - state.lastObservedWidth) <= 0.5 &&
        Math.abs(rect.height - state.lastObservedHeight) <= 0.5
      ) {
        return;
      }

      state.lastObservedWidth = rect.width;
      state.lastObservedHeight = rect.height;

      const containerStyle = getComputedStyle(el);
      const padL = parseFloat(containerStyle.paddingLeft) || 0;
      const padR = parseFloat(containerStyle.paddingRight) || 0;
      const contentWidth = rect.width - padL - padR;
      if (contentWidth <= 0) return;

      const { columns, gap } = this.getGridSettings(object);
      const columnWidth = (contentWidth - gap * (columns - 1)) / columns;

      const layoutUnchanged =
        columns === state.lastLayoutColumns &&
        gap === state.lastLayoutGap &&
        Math.abs(columnWidth - state.lastLayoutColumnWidth) <= 0.5;

      if (layoutUnchanged) return;

      if (state.resizeDebounceId) window.clearTimeout(state.resizeDebounceId);
      state.resizeDebounceId = window.setTimeout(() => {
        state.isResizing = true;
        this.scheduleLayout(object, true, "resize-observer");
        state.isResizing = false;
      }, RESIZE_DEBOUNCE_MS);
    };

    const moCallback: MutationCallback = (muts) => {
      const state = this.states.get(object);
      if (!state) return;
      let needsLayout = false;

      for (const m of muts) {
        if (m.removedNodes.length) {
          m.removedNodes.forEach((n) => {
            if (n.nodeType === 1) {
              const htmlEl = n as HTMLElement;
              this.cleanupImgListeners(state, htmlEl);
              state.items.delete(htmlEl);
              needsLayout = true;
            }
          });
        }
        if (m.addedNodes.length) {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === 1) {
              this.attachImgLoaders(object, state, n as HTMLElement);
              needsLayout = true;
            }
          });
        }
      }
      if (needsLayout) this.scheduleLayout(object, true, "mutation-observer");
    };

    const state: MasonryState = {
      ro: new ResizeObserver(roCallback),
      mo: new MutationObserver(moCallback),
      isResizing: false,
      lastObservedWidth: initialRect.width,
      lastObservedHeight: initialRect.height,
      lastLayoutColumns: 0,
      lastLayoutGap: 0,
      lastLayoutColumnWidth: -1,
      lastChildrenHash: 0,
      lastHeightsHash: 0,
      childIds: new WeakMap(),
      nextChildId: 1,
      imgUnsubs: new WeakMap(),
      layoutScheduled: false,
      items: new Map(),
      startHeight: el.offsetHeight,
      targetHeight: el.offsetHeight,
      heightStartTime: 0,
      heightDuration: CONSTANTS.DEFAULT_DURATION,
      heightEase: this.parseEasing(null),
      isAnimatingHeight: false,
      isAnimating: false,
      pendingHeight: undefined,
      pendingHeightAnimate: false,
      expectedEndTime: 0,
    };

    state.ro.observe(el);
    state.mo.observe(el, { childList: true });

    return state;
  }

  private handleAnimationEnd(object: StringObject, state: MasonryState, timeNow: number) {
    if (state.pendingHeight !== undefined) {
      const height = state.pendingHeight;
      const animateHeight = state.pendingHeightAnimate;
      state.pendingHeight = undefined;
      state.pendingHeightAnimate = false;

      if (animateHeight) {
        state.startHeight = object.htmlElement.offsetHeight;
        state.targetHeight = height;
        state.heightStartTime = timeNow;
        state.heightDuration = CONSTANTS.DEFAULT_DURATION;
        state.heightEase = this.parseEasing(null);
        state.isAnimatingHeight = true;
        state.isAnimating = true;
        return;
      }
      styleTxn.run(() => {
        styleTxn.setProps(object.htmlElement, { height: `${height}px` });
      });
    }

    if (state.isAnimating) {
      this.events.emit("masonry:shuffle:end", { object });
    }
    state.isAnimating = false;

    this.objectManager.refreshLayoutForRoot(object.htmlElement);

    if (state.endTimerId) {
      window.clearTimeout(state.endTimerId);
      state.endTimerId = undefined;
    }
    requestAnimationFrame(() => {
      this.events.emit("resize", true);
    });
  }

  private scheduleLayout(object: StringObject, animate: boolean, reason: string): void {
    const state = this.states.get(object);
    if (!state || state.layoutScheduled) return;

    state.layoutScheduled = true;

    frameDOM.measure(() => {
      state.layoutScheduled = false;
      this.performSyncLayout(object, state, animate, reason);
    });
  }

  /**
   * Performs the layout calculation and application synchronously.
   * Optimizes for batched DOM reads and writes via `styleTxn`.
   */
  private performSyncLayout(
    object: StringObject,
    state: MasonryState,
    animate: boolean,
    reason: string,
  ): void {
    const container = object.htmlElement as HTMLElement;
    const children = Array.from(container.children) as HTMLElement[];
    if (children.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerStyle = getComputedStyle(container);
    const padL = parseFloat(containerStyle.paddingLeft) || 0;
    const padR = parseFloat(containerStyle.paddingRight) || 0;
    const padT = parseFloat(containerStyle.paddingTop) || 0;
    const contentWidth = containerRect.width - padL - padR;

    if (contentWidth <= 0) return;

    const { columns, gap } = this.getGridSettings(object);
    const columnWidth = (contentWidth - gap * (columns - 1)) / columns;
    const mode = object.getProperty<string>("masonry-mode") || "auto";
    const allowHeightAnim =
      mode === "auto" &&
      animate &&
      !state.isResizing &&
      reason !== "window-resize" &&
      reason !== "resize-observer";

    const layoutParamsSame =
      columns === state.lastLayoutColumns &&
      gap === state.lastLayoutGap &&
      Math.abs(columnWidth - state.lastLayoutColumnWidth) <= 0.5;

    let childrenHash = 0;
    children.forEach((item) => {
      let id = state.childIds.get(item);
      if (!id) {
        id = state.nextChildId++;
        state.childIds.set(item, id);
      }
      childrenHash = (childrenHash * 31 + id) >>> 0;
    });

    state.lastLayoutColumns = columns;
    state.lastLayoutGap = gap;
    state.lastLayoutColumnWidth = columnWidth;
    state.lastChildrenHash = childrenHash;

    const prevWidths = new Map<HTMLElement, number>();
    if (animate) {
      children.forEach((item) => {
        const s = state.items.get(item);
        if (s) prevWidths.set(item, s.cw);
      });
    }

    styleTxn.begin();
    children.forEach((item) => {
      styleTxn.setProps(item, {
        position: "absolute",
        top: "0",
        left: "0",
        width: `${columnWidth}px`,
      });
    });
    styleTxn.commit();

    const itemHeights = children.map((item) => item.offsetHeight);
    let heightsHash = 0;
    itemHeights.forEach((h) => (heightsHash = (heightsHash * 31 + Math.round(h * 2)) >>> 0));

    state.lastHeightsHash = heightsHash;

    if (animate && prevWidths.size > 0) {
      styleTxn.run(() => {
        prevWidths.forEach((width, item) => {
          styleTxn.setProps(item, { width: `${width}px` });
        });
      });
    }

    const colHeights = new Array(columns).fill(0);
    const now = performance.now();
    let hasMovement = false;
    let maxMoveDuration = 0;

    children.forEach((item, index) => {
      const h = itemHeights[index];

      let colIndex = 0;
      let minH = colHeights[0];
      for (let i = 1; i < columns; i++) {
        if (colHeights[i] < minH) {
          minH = colHeights[i];
          colIndex = i;
        }
      }

      const targetX = colIndex * (columnWidth + gap) + padL;
      const targetY = minH + padT;
      const targetW = columnWidth;

      colHeights[colIndex] = minH + h + gap;

      const posDuration = parseTime(item.getAttribute("string-masonry-position-time"));
      const sizeDuration = parseTime(item.getAttribute("string-masonry-size-time"));

      const invPosDur = posDuration > 0 ? 1 / posDuration : 1000;
      const invSizeDur = sizeDuration > 0 ? 1 / sizeDuration : 1000;

      const posEase = this.parseEasing(item.getAttribute("string-masonry-position-easing"));
      const sizeEase = this.parseEasing(item.getAttribute("string-masonry-size-easing"));

      let itemState = state.items.get(item);

      if (!itemState) {
        const startX = animate ? targetX : targetX;
        const startY = animate ? targetY + 30 : targetY;

        itemState = {
          sx: startX,
          sy: startY,
          sw: targetW,
          tx: targetX,
          ty: targetY,
          tw: targetW,
          dx: 0,
          dy: 0,
          dw: 0,
          cx: startX,
          cy: startY,
          cw: targetW,
          startTime: now,
          posEase,
          invPosDur,
          sizeEase,
          invSizeDur,
          isMoving: animate,
        };
        state.items.set(item, itemState);

        if (!animate) {
          styleTxn.run(() => {
            styleTxn.setProps(item, {
              transform: `translate3d(${targetX}px, ${targetY}px, 0)`,
              width: `${targetW}px`,
              willChange: "auto",
            });
          });
        } else {
          itemState.dx = itemState.tx - itemState.sx;
          itemState.dy = itemState.ty - itemState.sy;
          itemState.dw = itemState.tw - itemState.sw;
          hasMovement = true;
          maxMoveDuration = Math.max(maxMoveDuration, posDuration, sizeDuration);
        }
      } else {
        const dist =
          Math.abs(itemState.tx - targetX) +
          Math.abs(itemState.ty - targetY) +
          Math.abs(itemState.tw - targetW);

        if (dist > 0.5) {
          itemState.sx = itemState.cx;
          itemState.sy = itemState.cy;
          itemState.sw = itemState.cw;

          itemState.tx = targetX;
          itemState.ty = targetY;
          itemState.tw = targetW;

          itemState.dx = itemState.tx - itemState.sx;
          itemState.dy = itemState.ty - itemState.sy;
          itemState.dw = itemState.tw - itemState.sw;

          itemState.posEase = posEase;
          itemState.invPosDur = invPosDur;
          itemState.sizeEase = sizeEase;
          itemState.invSizeDur = invSizeDur;

          itemState.startTime = now;
          itemState.isMoving = true;
          hasMovement = true;
          maxMoveDuration = Math.max(maxMoveDuration, posDuration, sizeDuration);
        }
      }
    });

    const newContainerHeight = Math.max(...colHeights);

    if (Math.abs(state.targetHeight - newContainerHeight) > 1) {
      if (animate) {
        const rect = container.getBoundingClientRect();
        const viewportHeight = this.data.viewport.windowHeight;
        var isFromBottom = false;
        if (rect.bottom >= -100 && rect.bottom <= viewportHeight + 100) {
          const scrollAdjustment = rect.top + newContainerHeight - viewportHeight;
          if (Math.abs(scrollAdjustment) > 1 && newContainerHeight < container.offsetHeight) {
            const targetScroll =
              rect.top + this.data.scroll.current + newContainerHeight - viewportHeight;
            this.events.emit("scrollTo", targetScroll);
            isFromBottom = true;
          }
        }

        if (newContainerHeight >= state.targetHeight) {
          if (allowHeightAnim) {
            state.startHeight = container.offsetHeight;
            state.heightStartTime = now;
            state.heightDuration = CONSTANTS.DEFAULT_DURATION;
            state.heightEase = this.parseEasing(null);
            state.isAnimatingHeight = true;
          } else {
            styleTxn.run(() => {
              styleTxn.setProps(object.htmlElement, { height: `${newContainerHeight}px` });
              requestAnimationFrame(() => {
                this.events.emit("resize", true);
              });
            });
          }
          state.targetHeight = newContainerHeight;
          state.pendingHeight = undefined;
        } else {
          state.pendingHeight = newContainerHeight;
          state.pendingHeightAnimate = allowHeightAnim;
          state.targetHeight = newContainerHeight;

          if (!isFromBottom) {
            styleTxn.run(() => {
              styleTxn.setProps(object.htmlElement, { height: `${newContainerHeight}px` });
              requestAnimationFrame(() => {
                this.events.emit("resize", true);
              });
            });
          }
        }

        hasMovement = true;
      } else {
        state.targetHeight = newContainerHeight;
        state.pendingHeight = undefined;
        styleTxn.run(() => {
          styleTxn.setProps(object.htmlElement, { height: `${newContainerHeight}px` });
        });
      }
    }

    if (hasMovement) {
      if (!state.isAnimating) {
        this.events.emit("masonry:shuffle:start", { object });
      }
      state.isAnimating = true;

      if (maxMoveDuration > 0) {
        state.expectedEndTime = now + maxMoveDuration + 32;
        if (state.endTimerId) window.clearTimeout(state.endTimerId);

        state.endTimerId = window.setTimeout(() => {
          if (!state.isAnimating) return;
          if (state.pendingHeight !== undefined || state.isAnimatingHeight) return;
          this.events.emit("masonry:shuffle:end", { object });
          state.isAnimating = false;
          state.endTimerId = undefined;
        }, maxMoveDuration + 50);
      }
    }
  }

  private getGridSettings(object: StringObject): GridSettings {
    const ww = this.data.viewport.windowWidth;
    const mode = object.getProperty<string>("masonry-mode") || "auto";

    if (mode === "manual") {
      const manualCols = object.getProperty<number>(CONSTANTS.MANUAL_COLS_KEY);
      const manualGap = object.getProperty<number>(CONSTANTS.MANUAL_GAP_KEY);
      const colsData = object.getProperty<any>("masonry-cols") || [];
      const gapData = object.getProperty<any>("masonry-gap") || [];

      return {
        columns: Math.max(1, Math.round(manualCols ?? getResponsiveValue(colsData, ww) ?? 2)),
        gap: Math.max(0, manualGap ?? getResponsiveValue(gapData, ww) ?? 16),
      };
    }

    const colsData = object.getProperty<any>("masonry-cols") || [{ breakpoint: 0, value: 2 }];
    const gapData = object.getProperty<any>("masonry-gap") || [{ breakpoint: 0, value: 16 }];

    return {
      columns: Math.max(1, Math.round(getResponsiveValue(colsData, ww) ?? 2)),
      gap: Math.max(0, getResponsiveValue(gapData, ww) ?? 16),
    };
  }

  private attachImgLoaders(object: StringObject, state: MasonryState, root: HTMLElement): void {
    const imgs = root.tagName === "IMG" ? [root] : Array.from(root.querySelectorAll("img"));
    imgs.forEach((img: Element) => {
      const imageEl = img as HTMLImageElement;
      if (imageEl.complete) return;
      if (state.imgUnsubs.has(imageEl)) return;

      const handler = () => {
        this.scheduleLayout(object, true, "img-load");
        this.cleanupImgListeners(state, imageEl);
      };

      imageEl.addEventListener("load", handler);
      imageEl.addEventListener("error", handler);

      state.imgUnsubs.set(imageEl, () => {
        imageEl.removeEventListener("load", handler);
        imageEl.removeEventListener("error", handler);
      });
    });
  }

  private cleanupImgListeners(state: MasonryState, root: HTMLElement): void {
    const cleanupSingle = (el: HTMLElement) => {
      const unsub = state.imgUnsubs.get(el);
      if (unsub) {
        unsub();
        state.imgUnsubs.delete(el);
      }
    };
    if (root.tagName === "IMG") cleanupSingle(root);
    else root.querySelectorAll("img").forEach((img) => cleanupSingle(img as HTMLElement));
  }
}
